import { Injectable } from '@angular/core';
import { PaletteRegistryService } from './palette-registry.service';

@Injectable({
  providedIn: 'root'
})
export class BeadImageService {
  private imageAvailabilityCache: Map<string, boolean> = new Map();
  private pendingChecks: Map<string, Promise<boolean>> = new Map();

  constructor(private paletteRegistry: PaletteRegistryService) {}

  /**
   * Get the folder name for a given palette ID
   */
  getFolderForPalette(paletteId: number): string | null {
    return this.paletteRegistry.getBeadImageFolder(paletteId);
  }

  /**
   * Check if a palette has bead images available
   */
  hasPaletteImages(paletteId: number): boolean {
    return this.paletteRegistry.getBeadImageFolder(paletteId) !== null;
  }

  /**
   * Generate the image path for a bead
   */
  getBeadImagePath(paletteId: number, beadName: string): string | null {
    const folder = this.getFolderForPalette(paletteId);
    if (!folder) {
      return null;
    }
    return `assets/bead-images/${folder}/${beadName}.jpg`;
  }

  /**
   * Check if a bead image exists (with caching)
   */
  async checkImageExists(paletteId: number, beadName: string): Promise<boolean> {
    const imagePath = this.getBeadImagePath(paletteId, beadName);
    if (!imagePath) {
      return false;
    }

    // Check cache first
    if (this.imageAvailabilityCache.has(imagePath)) {
      return this.imageAvailabilityCache.get(imagePath)!;
    }

    // Check if there's already a pending check for this image
    if (this.pendingChecks.has(imagePath)) {
      return this.pendingChecks.get(imagePath)!;
    }

    // Create a new check
    const checkPromise = new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => {
        this.imageAvailabilityCache.set(imagePath, true);
        this.pendingChecks.delete(imagePath);
        resolve(true);
      };
      img.onerror = () => {
        this.imageAvailabilityCache.set(imagePath, false);
        this.pendingChecks.delete(imagePath);
        resolve(false);
      };
      img.src = imagePath;
    });

    this.pendingChecks.set(imagePath, checkPromise);
    return checkPromise;
  }

  /**
   * Clear the image availability cache
   */
  clearCache(): void {
    this.imageAvailabilityCache.clear();
    this.pendingChecks.clear();
  }
}
